import type { ItemQuoteInput, TransactionIdentifier } from "@opensea/graphql"
import { graphql, useQuery } from "@opensea/graphql"
import { Flex } from "@opensea/ui-kit"
import { omit } from "es-toolkit/object"
import { useEffect, useMemo, useState } from "react"
import { useTranslations } from "use-intl"
import { useSnapshot } from "valtio"
import { Button } from "../../../../../components/Button"
import { TransactionFullScreen } from "../../../../../components/TransactionFullScreen"
import { TransactionReceipt } from "../../../../../components/TransactionReceipt"
import { TransactionReceiptFragment } from "../../../../../components/TransactionReceipt/TransactionReceipt.fragment"
import { ViewItemsOnProfileButton } from "../../../../../components/ViewItemsOnProfileButton"
import { FlowState } from "../../../../../stores/flowStore"
import {
  useSetReceiptStatus,
  useSetReceiptStatusFragment,
} from "../../../../hooks/useSetReceiptStatus"

const FETCH_INTERVAL = 500

const query = graphql(
  `
    query CollectionSweepFlowStatusQuery(
      $transactionIdentifiers: [TransactionIdentifier!]!
      $itemQuotes: [ItemQuoteInput!]!
    ) {
      sweepCollectionReceipt(
        transactionIdentifiers: $transactionIdentifiers
        itemQuotes: $itemQuotes
      ) {
        status
        itemReceipts {
          __typename
          item {
            id
          }
        }
        failedItemReceipts {
          __typename
          item {
            id
          }
        }
        ...useSetReceiptStatus
        ...TransactionReceipt
      }
    }
  `,
  [TransactionReceiptFragment, useSetReceiptStatusFragment],
)

type Props = {
  transactionIdentifiers: TransactionIdentifier[]
  collectionImageUrl: string | null
  itemQuotes: ItemQuoteInput[]
  itemImageMap: Record<string, string | null>
}

export const CollectionSweepStatus = ({
  transactionIdentifiers,
  collectionImageUrl,
  itemQuotes,
  itemImageMap,
}: Props) => {
  const t = useTranslations("wallet.CollectionSweepStatus")

  const [result, reexecuteQuery] = useQuery({
    query,
    variables: {
      transactionIdentifiers,
      itemQuotes,
    },
    pause: true,
  })

  useEffect(
    function pollForReceipt() {
      if (
        result.fetching ||
        (result.data?.sweepCollectionReceipt &&
          result.data.sweepCollectionReceipt.status !== "PENDING")
      ) {
        return
      }

      const timerId = setTimeout(() => {
        reexecuteQuery({ requestPolicy: "network-only" })
      }, FETCH_INTERVAL)

      return () => clearTimeout(timerId)
    },
    [result.fetching, reexecuteQuery, result.data?.sweepCollectionReceipt],
  )

  const receipt = result.data?.sweepCollectionReceipt

  useSetReceiptStatus(receipt)

  const [receiptOpen, setReceiptOpen] = useState(false)

  if (!receipt || receipt.status === "PENDING") {
    return (
      <TransactionFullScreen
        body={t("pending.description")}
        images={[collectionImageUrl]}
        loading
        title={t("pending.title")}
      />
    )
  }

  if (receipt.status === "FAILED") {
    return (
      <TransactionFullScreen
        body={t("failed.description")}
        bottom={
          receiptOpen ? (
            <TransactionReceipt flowType="purchase" receipt={receipt} />
          ) : (
            <Button onClick={() => setReceiptOpen(true)} variant="secondary">
              {t("viewReceipt")}
            </Button>
          )
        }
        images={[collectionImageUrl]}
        monochrome
        title={t("failed.title")}
      />
    )
  }

  if (receipt.status === "PARTIAL_SUCCESS") {
    return (
      <TransactionFullScreen
        body={t("partialSuccess.description", {
          itemsPurchasedCount: receipt.itemReceipts.length,
          itemsFailedCount: receipt.failedItemReceipts.length,
        })}
        bottom={
          receiptOpen ? (
            <TransactionReceipt flowType="purchase" receipt={receipt} />
          ) : (
            <Button onClick={() => setReceiptOpen(true)} variant="secondary">
              {t("viewReceipt")}
            </Button>
          )
        }
        images={receipt.itemReceipts.map(item =>
          item.item ? itemImageMap[item.item.id] : null,
        )}
        title={t("partialSuccess.title")}
      />
    )
  }

  return (
    <TransactionFullScreen
      bottom={
        receiptOpen ? (
          <TransactionReceipt flowType="purchase" receipt={receipt} />
        ) : (
          <Flex className="gap-4">
            <Button onClick={() => setReceiptOpen(true)} variant="secondary">
              {t("viewReceipt")}
            </Button>
            <ViewItemsOnProfileButton
              itemIds={receipt.itemReceipts.flatMap(itemReceipt =>
                itemReceipt.item ? [itemReceipt.item.id] : [],
              )}
            />
          </Flex>
        )
      }
      // Add back when we add social links
      // body={t("success.description")}
      confetti
      images={receipt.itemReceipts.map(item =>
        item.item ? itemImageMap[item.item.id] : null,
      )}
      title={t("success.title")}
    />
  )
}

type FlowStatusProps = {
  collectionImageUrl: string | null
}

export const CollectionSweepFlowStatus = ({
  collectionImageUrl,
}: FlowStatusProps) => {
  const { receiptTransactions, itemQuotes, itemImageMap } =
    useSnapshot(FlowState)

  const itemQuoteInputs = useMemo(
    () => itemQuotes.map(itemQuote => omit(itemQuote, ["orderId"])),
    [itemQuotes],
  )

  return (
    <CollectionSweepStatus
      collectionImageUrl={collectionImageUrl}
      itemImageMap={itemImageMap}
      itemQuotes={itemQuoteInputs}
      transactionIdentifiers={receiptTransactions.map(txn => ({
        chain: txn.chain,
        transactionHash: txn.hash,
      }))}
    />
  )
}
